import { Component, OnInit, ElementRef, ViewChild, AfterViewChecked, NgZone, ChangeDetectorRef } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { CommonService } from 'src/app/services/common.service';

interface MessageResponsePair {
  message: string;
  response?: string;
  loading?: boolean;
}

@Component({
  selector: 'app-techsure-queries',
  templateUrl: './techsure-queries.component.html'
})
export class TechsureQueriesComponent implements OnInit, AfterViewChecked {
  @ViewChild('chatContainer') private chatContainer!: ElementRef;
  
  inputText: string = '';
  messageResponsePairs: MessageResponsePair[] = [];
  isResponseLoading: boolean = false;
  apiError: string | null = null;

  // Voice Module State
  isListening: boolean = false;
  isProcessing: boolean = false;
  voiceError: string = '';
  mediaRecorder: any;
  audioChunks: BlobPart[] = [];
  audioBlob!: Blob;

  // Pre-defined suggestions
  suggestedItems: string[] = [
    'Explain gate criteria in simple terms',
    'Summarise Day 1 induction',
    'Quiz me on medical underwriting'
  ];

  constructor(
    private apiService: ApiService,
    public commonService: CommonService,
    private ngZone: NgZone,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!sessionStorage.getItem('sessionId')) {
      this.commonService.initializeSession();
    }
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    try {
      this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
    } catch (err) {}
  }

  selectSuggestion(suggestion: string): void {
    this.inputText = suggestion;
    this.sendMessage();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage(): void {
    if (!this.inputText.trim() || this.isResponseLoading) return;

    const query = this.inputText.trim();
    this.inputText = ''; 
    
    const newPair: MessageResponsePair = { message: query, loading: true };
    this.messageResponsePairs.push(newPair);
    this.isResponseLoading = true;
    this.apiError = null;

    const payload = {
      session_id: sessionStorage.getItem('sessionId'),
      query: query
    };

    this.apiService.getTrainingChatResponse(payload).subscribe({
      next: (res) => {
        newPair.loading = false;
        newPair.response = this.formatTextToHTML(res.response);
        this.isResponseLoading = false;
      },
      error: (err) => {
        newPair.loading = false;
        this.isResponseLoading = false;
        this.apiError = "We could not process your query. Please try again.";
      }
    });
  }

  formatTextToHTML(text: string): string {
    let formattedText = text.replace(/\n\n/g, '<br/><br/>');
    formattedText = formattedText.replace(/\n/g, '<br/>');
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
    return formattedText;
  }

  adjustTextareaHeight(event?: Event): void {
    // Allows passing either an event object or picking the element directly
    const target = event ? (event.target as HTMLTextAreaElement) : document.getElementById('chatInput') as HTMLTextAreaElement;
    if (target) {
      target.style.height = '40px'; 
      target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
    }
  }

  // ==========================================
  // VOICE MODULE INTEGRATION
  // ==========================================

  toggleVoiceRecognition(): void {
    if (!this.isListening) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  startRecording(): void {
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        this.mediaRecorder = new MediaRecorder(stream);
        this.mediaRecorder.start();
        this.isListening = true;
        this.voiceError = '';
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event: any) => {
          this.audioChunks.push(event.data);
        };
      })
      .catch(error => {
        this.voiceError = 'Microphone access denied';
      });
  }

  stopRecording(): void {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.isListening = false;
      this.mediaRecorder.onstop = () => {
        this.audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
        this.isProcessing = true;
        this.cdRef.detectChanges();
        this.sendAudioToApi(this.audioBlob);
      };
    }
  }

  sendAudioToApi(audioBlob: Blob): void {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.wav');
    formData.append('language_code', 'en');

    this.apiService.getTranscriptionForTraining(formData).subscribe({
      next: (res) => {
        this.ngZone.run(() => {
          this.inputText += (this.inputText.length > 0 ? ' ' : '') + res.transcription;
          this.isProcessing = false;
          
          setTimeout(() => {
            this.adjustTextareaHeight();
          });
          this.cdRef.detectChanges();
        });
      },
      error: (err) => {
        this.ngZone.run(() => {
          this.isProcessing = false;
          this.voiceError = 'Could not process audio. Try again.';
          this.cdRef.detectChanges();
        });
      }
    });
  }
}